import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send HttpOnly cookie on every request
});

// ── JWT Interceptor ──────────────────────────────────────────────────────────
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auto-refresh on 401 ──────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    if (error.response?.status === 401 && !original._retry && !original.url?.includes('/r/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }
      original._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        processQueue(null, data.token);
        original.headers.Authorization = `Bearer ${data.token}`;
        return api(original);
      } catch (err) {
        processQueue(err);
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// ── Auth API ─────────────────────────────────────────────────────────────────
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  me: () => api.get('/auth/me'),
  sendOtp: (mobile) => api.post('/auth/otp/send', { mobile }),
  verifyOtp: (mobile, otp) => api.post('/auth/otp/verify', { mobile, otp }),
  verifyOtpWidget: (mobile, accessToken) => api.post('/auth/otp/widget-verify', { mobile, accessToken }),
};

// ── User Management API ───────────────────────────────────────────────────────
export const userAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (data) => api.post('/users/change-password', data),
};

// ── Analytics API ─────────────────────────────────────────────────────────────
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview', { params }),
  getTrends: (params) => api.get('/analytics/trends', { params }),
  getDistribution: (params) => api.get('/analytics/distribution', { params }),
  getByLocation: () => api.get('/analytics/by-location'),
  getAuditLogs: (params) => api.get('/analytics/audit-logs', { params }),
};

// ── Legacy Vitals API ─────────────────────────────────────────────────────────
export const vitalsAPI = {
  getAllVitals: (params) => api.get('/vitals', { params }),
  getVitalsById: (id) => api.get(`/vitals/${id}`),
  getPatientHistory: (patientId) => api.get(`/vitals/patient/${patientId}`),
  createVitals: (data) => api.post('/vitals', data),
  updateVitals: (id, data) => api.put(`/vitals/${id}`, data),
  deleteVitals: (id) => api.delete(`/vitals/${id}`),
  getOverviewStats: () => api.get('/vitals/stats/overview'),
  getTrends: (params) => api.get('/vitals/stats/trends', { params }),
  getDistribution: () => api.get('/vitals/stats/distribution'),
};

// ── Location API ──────────────────────────────────────────────────────────────
export const locationAPI = {
  getAll: () => api.get('/locations'),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// ── Station API ───────────────────────────────────────────────────────────────
export const stationAPI = {
  getByLocation: (locationId) => api.get(`/stations/location/${locationId}`),
  create: (data) => api.post('/stations', data),
  update: (id, data) => api.put(`/stations/${id}`, data),
  updateTests: (id, assignedTests) => api.put(`/stations/${id}/tests`, { assignedTests }),
  bulkUpdateTests: (stations) => api.put('/stations/bulk-update/tests', { stations }),
  delete: (id) => api.delete(`/stations/${id}`),
};

// ── Test Types API ────────────────────────────────────────────────────────────
export const testTypeAPI = {
  getAll: () => api.get('/test-types'),
  getById: (testId) => api.get(`/test-types/${testId}`),
};

// ── Patient API ───────────────────────────────────────────────────────────────
export const patientAPI = {
  register: (data) => api.post('/patients/register', data),
  getAll: (params) => api.get('/patients', { params }),
  getById: (id) => api.get(`/patients/${id}`),
  getByQR: (qrCode) => api.get(`/patients/qr/${qrCode}`),
  getProgress: (id) => api.get(`/patients/${id}/progress`),
  getReport: (id) => api.get(`/patients/${id}/report`),
  update: (id, data) => api.put(`/patients/${id}`, data),
  exportExcel: (params) => api.get('/patients/export', { params, responseType: 'blob' }),
  getFamily: (id) => api.get(`/patients/${id}/family`),
};

// ── Session API ───────────────────────────────────────────────────────────────
export const sessionAPI = {
  getById: (id) => api.get(`/sessions/${id}`),
  getResults: (id) => api.get(`/sessions/${id}/results`),
  submitStation: (sessionId, stationId, data) =>
    api.put(`/sessions/${sessionId}/station/${stationId}`, data),
  getQueue: (locationId) => api.get(`/sessions/queue/${locationId}`),
  addRecommendation: (sessionId, data) => api.post(`/sessions/${sessionId}/recommendation`, data),
  getRecommendation: (sessionId) => api.get(`/sessions/${sessionId}/recommendation`),
};

// ── Dashboard API ─────────────────────────────────────────────────────────────
export const dashboardAPI = {
  getStats: (locationId) => api.get('/dashboard/stats', { params: locationId ? { locationId } : {} }),
  getQueue: (locationId) => api.get(`/dashboard/queue/${locationId}`),
};

// ── Tablet API ────────────────────────────────────────────────────────────────
export const tabletAPI = {
  register: (data) => api.post('/tablets/register', data),
  heartbeat: (id) => api.put(`/tablets/${id}/heartbeat`),
  getConfig: (id) => api.get(`/tablets/${id}/config`),
};

// ── MIS Report API ────────────────────────────────────────────────────────────
export const misAPI = {
  getMisReport: (params) => api.get('/reports/mis', { params }),
};

// ── Report API ────────────────────────────────────────────────────────────────
export const reportAPI = {
  getByPatientId: (id) => api.get(`/reports/patient/${id}`),
  getByMobile: (mobile) => api.get(`/reports/mobile/${mobile}`),
  getAllByMobile: (mobile) => api.get(`/reports/mobile/${mobile}/all`),
  downloadPdf: (patientId) => api.get(`/reports/patient/${patientId}/pdf`, { responseType: 'blob' }),
  notifyPatient: (patientId) => api.post(`/reports/patient/${patientId}/notify`),
  getShortCodeInfo: (code) => api.get(`/r/${code}`),
  sendShortLinkOtp: (code) => api.post(`/r/${code}/send-otp`),
  verifyShortLinkOtp: (code, otp) => api.post(`/r/${code}/verify-otp`, { otp }),
  testSms: (mobile, name) => api.post(`/reports/test-sms`, { mobile, name }),
};

export default api;

// ── PII Access Control Helpers ────────────────────────────────────────────────
/**
 * Returns true for superadmin, pii_admin, clinic_admin, and admin roles — all can see decrypted PII.
 * viewer role receives masked PII.
 */
export function isSuperAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.role === 'pii_admin';
  } catch {
    return false;
  }
}

export function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

/** Mask a patient name: "Ramesh Patil" → "R***** P****" */
export function maskName(name) {
  if (!name) return 'N/A';
  return name.split(' ').map(p => p.length <= 1 ? p : p[0] + '*'.repeat(p.length - 1)).join(' ');
}

/** Mask a mobile number: "9876543210" → "98****10" */
export function maskMobile(mobile) {
  if (!mobile) return '-';
  const s = String(mobile).replace(/\s/g, '');
  if (s.length <= 4) return '*'.repeat(s.length);
  return s.slice(0, 2) + '*'.repeat(s.length - 4) + s.slice(-2);
}

/** Mask an email: "john@gmail.com" → "j***@g****.com" */
export function maskEmail(email) {
  if (!email) return '-';
  const [local, domain] = email.split('@');
  if (!domain) return email[0] + '*'.repeat(email.length - 1);
  const maskedLocal = local.length <= 1 ? local : local[0] + '*'.repeat(local.length - 1);
  const [domainName, ...tld] = domain.split('.');
  const maskedDomain = domainName.length <= 1 ? domainName : domainName[0] + '*'.repeat(domainName.length - 1);
  return `${maskedLocal}@${maskedDomain}.${tld.join('.')}`;
}

/**
 * Apply masking to a patient object for non-superadmin users.
 * Returns a new object with name/mobile/email masked if user is not superadmin.
 */
export function applyPiiMask(patient) {
  if (isSuperAdmin()) return patient;
  return {
    ...patient,
    name:   maskName(patient?.name),
    mobile: maskMobile(patient?.mobile),
    email:  maskEmail(patient?.email),
  };
}
